import { flow, map } from 'lodash/fp'
import { delay, eventChannel } from 'redux-saga'
import { call, put, race, select, spawn, take, takeEvery, takeLatest, all } from 'redux-saga/effects'

import { sagas as analyticsSagas } from 'analytics'
import {
  APPLICATION_FETCH_UPDATE_ERROR,
  APPLICATION_FETCH_UPDATE_SUCCESS,
  APPLICATION_INSTALL_UPDATE_ERROR,
  APPLICATION_INSTALL_UPDATE_SUCCESS,
} from 'core/action-types'
import { applicationFetchUpdate, applicationCheckVersion } from 'core/actions'
import BI_MESSAGES from 'core/data-model/constants/bi-messages'
import DEFAULTS from 'core/data-model/constants/defaults'
import VALUE from 'core/data-model/constants/values'
import core from 'core/sagas'
import middlewareSvc from 'core/service/advisorzone-middleware/request-wrapper'
import { getValidationErrorMessage } from 'core/service/advisorzone-middleware/validation-error-message'
import { getAuthenticatedUserProfile } from 'core/service/identity'
import { isNil } from 'core/service/lib/type-check'
import { getAppConfig, compateLt } from 'deploy-env/app-config'
import { createNewApp, loadQuickQuote, updateEnhancedAddressStatus } from 'e-submission/apps/actions'
import * as services from 'e-submission/domain/service'
import { listMinimalApplicationByIdNo } from 'e-submission/domain/service/application'
import { retrievePolicyListByIdNo, retrievePolicyDetails } from 'core/service/retrieve-customer/policy-api'
import { filterPolicyList } from 'core/service/retrieve-customer/policy-list'
import {
  GET_DISTRIBUTOR_GROUPS_FAILED,
  GET_DISTRIBUTOR_GROUPS_SUCCESS,
  updateAdditionalUserDetails,
  updateUser,
  updateCheckNativeAppVersionUser,
  USER_EXPIRED,
  USER_FOUND,
  UPDATE_USER,
} from 'identity/actions'
import { getIdentityUser } from 'identity/selectors'
import { logoutFromIDP, logoutFromIAM } from 'identity/service'
import { isMobileOnline } from 'lib/cordova-util'

import { omit } from 'lodash/fp'
import {
  resetQuoteForm,
  backToHome,
  reloadApplicationRequest,
  resetISSocial,
  resetSellerProfile,
  setCloneAppList,
  setClonedPolicy,
  clearCloneAppList,
  setProcessingSuccess,
  RESET_QUOTE_FORM,
} from 'quick-quote/actions'
import { getNavTabList } from 'quick-quote/components/navigations/tabs'
import { PATH, ROUTE_PATH, fnaPath, qqPath } from 'quick-quote/constants/path'
import { getToggles } from 'quick-quote/feature-toggles'
import { sagas as fnaMarketConductSagas } from 'quick-quote/fna/market-conducts/sagas'
import { sagas as fnaSuitabilitySagas } from 'quick-quote/fna/suitability/sagas'
import { sagas as fnaNeedGapSagas } from 'quick-quote/fna/need-and-gap-analysis/sagas'
import { sagas as fnaSagas } from 'quick-quote/fna/sagas'
import { sagas as InsuredInformationSagas } from 'quick-quote/insured-information'
import { toQuickQuoteInterface } from 'quick-quote/interface'
import { setNotificationMessage } from 'quick-quote/notification/actions'
import { sagas as notificationSagas } from 'quick-quote/notification/sagas'
import { sagas as productBumnanReadySagas } from 'quick-quote/product-bumnan-ready/sagas'
import { sagas as productCommonBenefitIllustrationSagas } from 'quick-quote/product-common/benefit-illustration'
import { sagas as productWholeLifeCoveragePlanSagas } from 'quick-quote/product-common/coverage-plan'
import { sagas as productFlexiHealthCoveragePlanSagas } from 'quick-quote/product-flexi-health/coverage-plan'
import { sagas as productHealthCoveragePlanSagas } from 'quick-quote/product-health/coverage-plan'
import { sagas as productHealthToppingSagas } from 'quick-quote/product-health-topping/sagas'
import { sagas as productIHealthyUltraCoveragePlanSagas } from 'quick-quote/product-ihealthy-ultra/coverage-plan'
import { sagas as productIHealthyUltraSagas } from 'quick-quote/product-ihealthy-ultra/sagas'
import { sagas as productInvestmentBenefitIllustrationSagas } from 'quick-quote/product-investment/benefit-illustration/sagas'
import { sagas as productInvestmentCoveragePlanSagas } from 'quick-quote/product-investment/coverage-plan'
import { sagas as fundAllocationSagas } from 'quick-quote/product-investment/fund-allocation/sagas'
import { sagas as productLifeReadySagas } from 'quick-quote/product-life-ready/sagas'
import { sagas as productLifeProtectPlusSagas } from 'quick-quote/product-life-protect-plus/sagas'
import { sagas as productLifeSaveProBenefitSagas } from 'quick-quote/product-life-save-pro/benefit-illustration/sagas'
import { sagas as productLifeSaveProSagas } from 'quick-quote/product-life-save-pro/sagas'
import { sagas as productLoanBenefitIllustrationSagas } from 'quick-quote/product-loan/benefit-illustration/sagas'
import { sagas as productLoanCoveragePlanSagas } from 'quick-quote/product-loan/coverage-plan'
import { sagas as productPerfectLifeSagas } from 'quick-quote/product-perfect-life/sagas'
import { sagas as productProtectionSagas } from 'quick-quote/product-protection/coverage-plan/sagas'
import { sagas as productSavingSagas } from 'quick-quote/product-saving/coverage-plan/sagas'
import { sagas as productSelectionSagas } from 'quick-quote/product-selection/sagas'
import { getSelectedDisplayProduct, getSelectedDisplayProductCode } from 'quick-quote/product-selection/selectors'
import {
  UPDATE_MATURITY_VALIDATE,
  selectPolicyOwner,
  resetQuoteFormAfterMaturityValidate,
} from 'quick-quote/product-sio/actions'
import { sagas as productSIOSagas } from 'quick-quote/product-sio/sagas'
import { getMaturityValidate } from 'quick-quote/product-sio/selectors'
import { sagas as productSukhapabDekDeeSagas } from 'quick-quote/product-sukhapabdekdee/sagas'
import { sagas as productTermLifeSagas } from 'quick-quote/product-term-life/sagas'
import { sagas as productInvestmentBenefitTermLifeSagas } from 'quick-quote/product-term-life/benefit-illustration/sagas'
import { sagas as productMRTASagas } from 'quick-quote/product-mrta/sagas'
import { sagas as productPASagas } from 'quick-quote/product-pa/sagas'
import { sagas as optySagas } from 'quick-quote/opty/sagas'
import { sagas as mwPlusSagas } from 'quick-quote/my-wealth-plus/sagas'

import { sagas as benefitIllustrationSelectionSagas } from 'quick-quote/product-whole-life/benefit-illustration'
import { sagas as productLifeProtect18Sagas } from 'quick-quote/product-whole-life/sagas'
import { getAppState, getAppVersion, getIsUserOnline, getEnableRetrieveCustomerPolicy } from 'quick-quote/selectors'
import { sagas as uiSagas } from 'quick-quote/ui/sagas'
import { APIErrorWatcher } from 'quick-quote/util/api-watcher'
import { clearAllCordovaCookies } from 'lib/cordova-util'

import {
  BACK_TO_HOME,
  CLICK_HOME_BUTTON,
  CONNECT_TO_DATABASE_FAILED,
  CONNECT_TO_DATABASE_REQUEST,
  CONNECT_TO_DATABASE_SUCCESS,
  connectToDatabaseFailed,
  connectToDatabaseRequest,
  connectToDatabaseSuccess,
  GET_MODEL_FACTORS_FAILED,
  GET_MODEL_FACTORS_SUCCESS,
  GET_OCCUPATIONS_FAILED,
  GET_OCCUPATIONS_SUCCESS,
  GET_PRODUCTS_FAILED,
  GET_PRODUCTS_REQUEST,
  GET_PRODUCTS_SUCCESS,
  GET_RIDERS_FAILED,
  GET_RIDERS_REQUEST,
  GET_RIDERS_SUCCESS,
  REDIRECT_MA_PAGE,
  REDIRECT_OPTY_PAGE,
  getModelFactorsRequest,
  getOccupationsRequest,
  getProductsFailed,
  getProductsRequest,
  getProductsSuccess,
  getRidersFailed,
  getRidersRequest,
  getRidersSuccess,
  INITIALISE_APPLICATION_REQUEST,
  initialiseApplicationFailedWithNoInternet,
  initialiseApplicationServerIsDown,
  initialiseApplicationSuccess,
  LOGIN_REQUEST,
  LOGOUT_REQUEST,
  RELOAD_APPLICATION_REQUEST,
  setAllowedAgentTypes,
  setFnaConfiguration,
  setVersion,
  setViewConfiguration,
  START_REMOTE_SELLING,
  START_STANDARD_SELLING,
  UPDATE_APPLICATION_REQUEST,
  CHECK_VERSION_APP,
  SEARCH_APP_LIST_BY_ID,
  setCloneAppSearchStatus,
  redirectMaPage,
  redirectOptyPage,
  setEnableMaPage,
  GET_EIP_POLICY_DETAILS,
  setEnableRetrieveCustomerPolicy,
} from './actions'

import { isFNAPath, isQuickQuotePath } from './components/navigations/utils'
import { ENABLE_FNA_PHASE } from './fna/actions'
import { SET_ACCEPT_ILP_SALE_SHEET } from './ui/actions'
import { getDevice } from './util/getDevice'
import { analytics } from 'analytics/analytics'
import { IsRealDevice } from 'e-submission/utils'
import { v1 as generateUUID } from 'uuid'
import { logLoginDevice, logLogoutDevice } from 'e-submission/domain/service/utils'
import _ from 'lodash'
import ACTIONS from './portal/action-types'
import { getQuestionareSuitability } from '../core/service-wrapper/partner-api/service'

export const reloadApplication = (location) => location.reload(true)

export const updateApplication = (location) => {
  if (window.cordova) {
    if (window.cordova.platformId === 'ios') {
      return window.open('https://itunes.apple.com/us/app/advisorzone/id1287893930?ls=1&mt=8', '_self')
    } else {
      return window.open('https://play.google.com/store/apps/details?id=com.ktaxa.advisorzone', '_self')
    }
  }
  return location.reload(true)
}

export function* onReloadApplicationRequest(location) {
  yield call(reloadApplication, location)
}

export function* onUpdateApplicationRequest(location) {
  yield call(updateApplication, location)
}

/* eslint-disable no-console */
export function* onInitialiseApplicationRequest(action) {
  console.info('onInitialiseApplicationRequest')
  console.info('try to connectToDatabaseRequest')
  yield put(connectToDatabaseRequest(action.dbLocalUrl))
  console.info('connectToDatabaseRequest success')
  if (getToggles().ENABLE_AUTHORISATION_CODE_FLOW || getToggles().ENABLE_OFFLINE_ACCESS_FLOW_ONLY) {
    const openMaPage = yield* checkOpeningMaPage()
    yield put(setEnableMaPage(openMaPage && openMaPage.data.enableMaPage))
    yield put(setEnableRetrieveCustomerPolicy(openMaPage && openMaPage.data.enableRetrieveCustomerPolicy))
    if (openMaPage && openMaPage.data.enableMaPage) {
      yield put(redirectMaPage())
      return
    }
    const validateToken = yield* validateAcessToken()
    if (validateToken && validateToken.data.isTokenValid) {
      console.info('updateUserInfoAction')
      yield* updateUserInfoAction()
    } else {
      yield put(resetSellerProfile())
      localStorage.removeItem('userInfo')
      localStorage.removeItem('sessionState')
    }
  }
}
/* eslint-enable no-console */
/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* checkIfApplicationDataIsReady(serviceWrapper) {
  const [isAnyFailed, allNonEmpty] = yield call(serviceWrapper.checkApplicationDataReady)
  if (isAnyFailed) {
    yield put(reloadApplicationRequest())
    yield put(initialiseApplicationServerIsDown())
  } else if (allNonEmpty) {
    const appVersion = yield select(getAppVersion)

    const version = {
      appVersion,
    }

    console.warn(`appVersion: ${appVersion}`)

    if (isNil(appVersion) || appVersion === '') {
      yield put(reloadApplicationRequest())
      yield put(initialiseApplicationServerIsDown())
    } else {
      yield put(setVersion(version))
      yield put(setAllowedAgentTypes(getAppConfig().ALLOWED_AGENT_TYPES_FOR_ESUBMISSION))
      yield put(initialiseApplicationSuccess())
    }
  }
}

export function* onAnyRequiredDataRequestFailed(action) {
  const isUserOnline = yield select(getIsUserOnline)

  if (isUserOnline) {
    yield put(reloadApplicationRequest())
    yield put(initialiseApplicationServerIsDown())
  } else {
    yield put(initialiseApplicationFailedWithNoInternet())
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 * @param action
 */
export function* onConnectToDatabaseRequest(serviceWrapper, action) {
  try {
    yield call(serviceWrapper.initial, action.dbLocalUrl)
    yield put(connectToDatabaseSuccess())
  } catch (error) {
    console.error(error)
    yield put(connectToDatabaseFailed())
  }
}

export function* onLogIn(userManager) {
  try {
    if (!isMobileOnline()) {
      throw new Error('Network is offline')
    }

    let deviceUUID = localStorage.getItem('deviceUUID')
    console.log('old deviceUUID', deviceUUID)
    if (_.isNil(deviceUUID)) {
      if (!window.cordova) {
        console.log('web deviceUUID', generateUUID())
        localStorage.setItem('deviceUUID', generateUUID())
      } else {
        console.log('mobile deviceUUID', window?.device?.uuid)
        localStorage.setItem('deviceUUID', window?.device?.uuid)
      }
    }
    console.log('deviceUUID', deviceUUID)
    deviceUUID = localStorage.getItem('deviceUUID')
    console.log('localStorage deviceUUID', deviceUUID)

    if (getToggles().ENABLE_AUTHORISATION_CODE_FLOW || getToggles().ENABLE_OFFLINE_ACCESS_FLOW_ONLY) {
      const loginWindowRef = yield* openLoginWindow()
      if (!window.cordova) {
        yield* waitForLoginWindowMessage()
        yield* ifLoginWindowClosed(loginWindowRef)
      } else {
        yield* userInfoListener(loginWindowRef)
        yield* watchCodovaInAppBrowser(loginWindowRef)
      }
      const userInfo = JSON.parse(localStorage.getItem('userInfo'))
      const user = yield getAuthenticatedUserProfile(userInfo)
      const logLoginDeviceResp = yield logLoginDevice(user, {
        userInfo: {
          deviceUUID: localStorage.getItem('deviceUUID'),
          device_type: window.cordova ? 'mobile' : 'browser',
          auth_time: userInfo.auth_time,
          agent_code: userInfo.agent_code,
          agent_type: userInfo.agent_type,
        },
      })
      if (logLoginDeviceResp?.error?.message === 'LOGIN_LIMIT_SESSION_EXCEEDED') {
        const deviceType = logLoginDeviceResp?.error?.deviceType
        const numberOfLimit = logLoginDeviceResp?.error?.numberOfLimit
        yield put(setNotificationMessage({ type: 'LoginLimitSessionExceeded', deviceType, numberOfLimit }))
        yield put(resetSellerProfile())
        const user = yield getAuthenticatedUserProfile(userInfo)
        yield logLogoutDevice(user, {
          userInfo: {
            deviceUUID: localStorage.getItem('deviceUUID'),
            device_type: window.cordova ? 'mobile' : 'browser',
            auth_time: userInfo.auth_time,
            agent_code: userInfo.agent_code,
            agent_type: userInfo.agent_type,
          },
        })
        localStorage.removeItem('userInfo')
        localStorage.removeItem('sessionState')
      }
      analytics.sendCustomEvent({
        category: 'Identity',
        action: 'Login_User_Type',
        label: userInfo.agent_type ? userInfo.agent_type : 'AGENT',
      })
    } else {
      yield userManager.signinPopup()
    }

    console.log('check APPLICATION_CHECK_VERSION_VALIDVERSION >>> after')
  } catch (err) {
    if (err.message.indexOf('Network') !== -1) {
      yield put(setNotificationMessage({ type: 'Warning', subType: 'NoInternet' }))
    }
  }
}

export function* openLoginWindow() {
  const middlewareLoginURL = `${getAppConfig().ADVISORZONE_MIDDLEWARE}/login`
  const appVersion = getAppVersion()
  const device = getDevice()
  let queryString = `?isRealDevice=${IsRealDevice}`
  if (getToggles().ENABLE_CHECK_NATIVE_APPVERSION) {
    queryString = `?applicationVersion=${appVersion}&device=${device}&isRealDevice=${IsRealDevice}`
  }
  const loginWindow = yield window.cordova
    ? window.cordova.InAppBrowser.open(`${middlewareLoginURL}/mobile${queryString}`, '_blank', 'location=no,zoom=no')
    : window.open(`${middlewareLoginURL}/web${queryString}`)

  if (getToggles().ENABLE_LODING_SPINNER_CORDOVA_IAB && window.cordova) {
    yield loginWindow.addEventListener('loadstart', (params, callback) => {
      window.plugins.spinnerDialog.show()
    })
    yield loginWindow.addEventListener('loadstop', (event) => {
      window.plugins.spinnerDialog.hide()
      loginWindow.show()
    })
  }

  return loginWindow
}

function* waitForLoginWindowMessage() {
  localStorage.removeItem('userInfo')
  localStorage.removeItem('sessionState')
  window.addEventListener('message', handlePostMessage, true)
}

function* userInfoListener(cordovaWindow) {
  localStorage.removeItem('userInfo')
  localStorage.removeItem('sessionState')
  yield cordovaWindow.addEventListener('loadstop', (event) => {
    const loop = setInterval(() => {
      cordovaWindow.executeScript({ code: 'localStorage.getItem( "userInfo" )' }, (values) => {
        const value = values[0]
        if (value === '"logout"') {
          if (window.cordova) {
            clearAllCordovaCookies()
              .then(() => {
                cordovaWindow.close()
              })
              .catch((error) => {
                cordovaWindow.close()
              })
              .finally(() => {
                window.location.reload()
              })
          } else {
            logoutFromIDP()
            clearInterval(loop)
            window.location.reload()
            setTimeout(() => {
              cordovaWindow.close()
            }, 1000)
          }
        } else if (value) {
          clearInterval(loop)
          localStorage.setItem('userInfo', value)
          setTimeout(() => {
            cordovaWindow.close()
          }, 1000)
        }
      })
    }, 1000)
  })
}

function* watchCodovaInAppBrowser(cordovaWindow) {
  const cordovaChannel = eventChannel((emit) => {
    cordovaWindow.addEventListener('exit', (event) => {
      emit({ window: { closed: true } })
    })

    return () => {
      cordovaWindow.removeEventListener('exit', (event) => {
        emit({ window: { closed: false } })
      })
    }
  })
  // prettier-ignore
  while (true) { // NOSONAR
    const cordovaEvent = yield take(cordovaChannel)
    if (cordovaEvent.window.closed) {
      yield* updateUserInfoAction()
      break
    }
  }
}

function handlePostMessage(event) {
  const middlewarePath =
    getAppConfig().ENV_LOCATION === 'DEV'
      ? getAppConfig().MAPPING_HOST_ADVISORZONE_MIDDLEWARE
      : getAppConfig().ADVISORZONE_MIDDLEWARE
  if (event.origin !== middlewarePath) return
  localStorage.setItem('userInfo', JSON.stringify(event.data))
  event.target.removeEventListener(event.type, handlePostMessage, true)
}

function handleLogoutPostMessage(event) {
  if (event.origin !== getAppConfig().ADVISORZONE_MIDDLEWARE) return
  localStorage.removeItem('userInfo')
  localStorage.removeItem('sessionState')
  event.target.removeEventListener(event.type, handleLogoutPostMessage, true)
}

function* waitForLogoutWindowMessage() {
  window.addEventListener('message', handleLogoutPostMessage, true)
}

function* ifLoginWindowClosed(loginWindow) {
  while (true) {
    if (yield loginWindow.closed) {
      yield* updateUserInfoAction()
      break
    }
    yield delay(1000)
  }
}

export function* updateUserInfoAction() {
  const storedValue = localStorage.getItem('userInfo')
  if (storedValue) {
    const idTokenInfo = JSON.parse(storedValue)
    const user = yield getAuthenticatedUserProfile(idTokenInfo)
    yield put(updateUser(user))
    yield put(updateAdditionalUserDetails())

    const userInfo = JSON.parse(localStorage.getItem('userInfo'))
    console.log('userInfo?.azMWMinVersion', userInfo.azMWMinVersion)
    let appVersion = yield select(getAppVersion)
    try {
      appVersion = yield window.getAppVersionByBridging()
    } catch (e) {
      console.error(`appVersion :`, e.message)
      return e
    } finally {
      console.log(`finish doing updateUserInfoAction`)
      yield delay(2000)
      yield put(redirectOptyPage())
    }

    if (getToggles().ENABLE_CHECK_TARGET_VERSION && IsRealDevice) {
      if (compateLt(appVersion, userInfo.azMWMinVersion)) {
        yield put(applicationCheckVersion())
        yield put(updateCheckNativeAppVersionUser())
      }
    }
  }
}

export function* validateAcessToken() {
  const storedValue = yield localStorage.getItem('userInfo')
  if (storedValue) {
    const idTokenInfo = yield JSON.parse(storedValue)
    return yield middlewareSvc.getRequest('auth/validate', idTokenInfo, {})
  } else {
    yield put({ type: LOGIN_REQUEST })
    return false
  }
}

export function* checkOpeningMaPage() {
  return yield middlewareSvc.getRequest('config', { ignoreAccessToken: true })
}

/**
 * @param userManager
 * @param history {ReturnType<import('history').createBrowserHistory>}
 */
export function* onLogOut(userManager, history) {
  yield put(resetSellerProfile())
  const userInfo = JSON.parse(localStorage.getItem('userInfo'))
  const user = yield getAuthenticatedUserProfile(userInfo)
  const userInfoExp = _.get(userInfo, 'exp')
  const exp = userInfoExp ? userInfoExp * 1000 : Date.now()
  const now = Date.now()
  const isTokenExpired = now > exp
  if (userInfo && !isTokenExpired) {
    yield logLogoutDevice(user, {
      userInfo: {
        deviceUUID: localStorage.getItem('deviceUUID'),
        auth_time: userInfo.auth_time,
        agent_code: userInfo.agent_code,
        agent_type: userInfo.agent_type,
      },
    })
  }
  if (getToggles().ENABLE_AUTHORISATION_CODE_FLOW || getToggles().ENABLE_OFFLINE_ACCESS_FLOW_ONLY) {
    const logoutWindow = yield* openLogoutWindow()
    if (!window.cordova) {
      yield* waitForLogoutWindowMessage()
      yield delay(500)
      yield* ifLogoutWindowClosed(logoutWindow, history)
    } else {
      yield* userInfoListener(logoutWindow)
    }
  } else {
    yield userManager
      .signoutPopup()
      .then(() => {
        logoutFromIDP()
        navigateAfterLogout(history)
      })
      .catch((err) => err)
  }
}

/**
 * @param history {ReturnType<import('history').createBrowserHistory>}
 */
function navigateAfterLogout(history) {
  const matchAuthorizeErrorPathPattern = /\/authorize\/error/g
  const pathName = _.get(history, 'location.pathname')
  if (matchAuthorizeErrorPathPattern.test(pathName)) {
    window.location.replace('/')
  } else {
    window.location.reload()
  }
}

export function* openLogoutWindow() {
  const middlewareLogoutURL = `${getAppConfig().ADVISORZONE_MIDDLEWARE}/logout`

  const logoutWindow = yield window.cordova
    ? window.cordova.InAppBrowser.open(
        `${middlewareLogoutURL}/mobile`,
        '_blank',
        'location=no,clearcache=yes,clearsessioncache=yes'
      )
    : window.open(`${middlewareLogoutURL}/web`, '_blank')
  return logoutWindow
}

function* ifLogoutWindowClosed(logoutWindow, history) {
  while (true) {
    if (yield logoutWindow.closed && isNil(localStorage.getItem('userInfo'))) {
      logoutFromIDP()
      if (getToggles().ENABLE_IAMC_IDP) {
        yield delay(2500)
      }
      navigateAfterLogout(history)
      break
    }
    yield delay(500)
  }
}

export function* watchAPIError() {
  // prettier-ignore
  while (true) { // NOSONAR
    const error = yield take(APIErrorWatcher)

    error.status === 403
      ? yield put(setNotificationMessage({ type: 'AuthenticationError' }))
      : yield put(
        setNotificationMessage({
          type: 'GenericError',
          message: getValidationErrorMessage(error.data),
          title: undefined,
        })
      )
  }
}

/**
 * @param esubStore
 * @param history
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* startEsub(esubStore, history, serviceWrapper) {
  const { dispatch } = esubStore
  if (getToggles().ENABLE_MANUAL_CORDOVA_UPDATE && window.chcp) {
    // start update
    yield put(applicationFetchUpdate())
    // wait for update procrss to completed
    const { fetchError } = yield race({
      fetchSuccess: take(APPLICATION_FETCH_UPDATE_SUCCESS),
      fetchError: take(APPLICATION_FETCH_UPDATE_ERROR),
    })
    // block go to esub if failed
    if (fetchError) {
      return
    }
    const { installError } = yield race({
      installSuccess: take(APPLICATION_INSTALL_UPDATE_SUCCESS),
      installError: take(APPLICATION_INSTALL_UPDATE_ERROR),
    })
    // block go to esub if failed
    if (installError) {
      return
    }
  }

  const qqState = yield select(getAppState)
  const { category } = yield select(getSelectedDisplayProduct)

  const biProps = yield call(serviceWrapper.products.COMMON.prepareBIProps, {
    category,
    biType: BI_MESSAGES.BI_TYPE_FULL,
  })
  const smallerBiProps = omit(['basicPlan.image'], biProps)
  const quickQuoteInterface = yield call(toQuickQuoteInterface, qqState, smallerBiProps)
  const user = yield select(getIdentityUser)

  yield call(dispatch, loadQuickQuote(quickQuoteInterface))
  yield call(dispatch, createNewApp(user))
  history.push(ROUTE_PATH.ESUBMISSION)
}

export function* startFnaPhase(history) {
  const state = yield select(getAppState)
  const tabList = getNavTabList({ state, location: history.location })
  const needTab = tabList.find((tab) => tab.path.indexOf(PATH.NEED_AND_GAP_ANALYSIS) > -1) || { path: '/' }
  // send user to need-and-gap
  history.push(needTab.path)
}

export function* handleAcceptIlpSlaeSheet(history) {
  const state = yield select(getAppState)
  const tabList = getNavTabList({ state, location: history.location })
  const nextTab = tabList.find((tab) => tab.path.indexOf(PATH.INSURED_INFORMATION) > -1) || { path: '/' }
  // send user to need-and-gap
  history.push(nextTab.path)
}

export function* goToMaPage(history) {
  history.push('/authorize/ma')
}

export function* goToOptyPage(history) {
  const deeplinkOptyId = sessionStorage.getItem('deeplinkOptyId')
  const deeplinkAgentId = sessionStorage.getItem('deeplinkAgentId')
  if (deeplinkOptyId && deeplinkAgentId) {
    sessionStorage.removeItem('deeplinkOptyId')
    sessionStorage.removeItem('deeplinkAgentId')
    history.push(`../opportunities/list?ag=${deeplinkAgentId}&opty=${deeplinkOptyId}`)
  }
}

export function* handleBackToHome(history) {
  yield put(resetQuoteForm())
  yield put(resetISSocial())
  history.push('/')
}

export function* onHomeButtonClick(history) {
  if (isFNAPath(history.location.pathname) || isQuickQuotePath(history.location.pathname)) {
    yield put(
      setNotificationMessage({
        type: 'ConfirmToHome',
      })
    )
  } else {
    yield put(backToHome())
  }
}

// clone application

export function* safeCall(fn, ...args) {
  try {
    const data = yield call(fn, ...args)
    return { data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export function* handleSearchApplicationListById(action) {
  const { idType, idNo } = action.payload
  const user = yield select(getIdentityUser)
  yield put(clearCloneAppList())

  try {
    yield put(setCloneAppSearchStatus('searching'))

    let response = []
    let responsePolicyList = []
    const enableRetrieveCustomerPolicy = yield select(getEnableRetrieveCustomerPolicy)
    if (enableRetrieveCustomerPolicy) {
      const [appResult, policyResult] = yield all([
        call(safeCall, listMinimalApplicationByIdNo, idType, idNo, user),
        call(safeCall, retrievePolicyListByIdNo, idType, idNo, user),
      ])
      response = appResult.data || []
      responsePolicyList = filterPolicyList(_.get(policyResult, 'data.RetrievePolicyListResponse.policy', []))
    } else {
      response = yield call(listMinimalApplicationByIdNo, idType, idNo, user)
    }

    const sortListByStatus = (prevApp, nextApp) => {
      const statusRank = (_status) => {
        switch (_status) {
          case 'submitted':
            return 1
          case 'signed':
            return 2
          default:
            return 3
        }
      }
      const prevAppStatusRank = statusRank(prevApp.status)
      const nextAppStatusRank = statusRank(nextApp.status)

      if (prevAppStatusRank - nextAppStatusRank === 0) {
        return new Date(nextApp.updatedAt) - new Date(prevApp.updatedAt)
      }
      return prevAppStatusRank - nextAppStatusRank
    }

    const appSummaries = flow(map(omit(['_rev'])))(response)
    appSummaries.sort(sortListByStatus)

    yield put(setCloneAppList({ applicationList: appSummaries, policyList: responsePolicyList }))
    yield put(setNotificationMessage({ type: 'SearchAppListModal' }))

    yield put(setCloneAppSearchStatus('success'))
  } catch (err) {
    const message = err.message
    const notificationMessage = getToggles().ENABLE_RED_TRUCK_ERROR_MSG
      ? { type: 'GenericError', message: message }
      : { type: 'GenericError' }
    yield put(setProcessingSuccess())
    yield put(setNotificationMessage(notificationMessage))

    yield put(setCloneAppSearchStatus('error'))
  }
}

export function* onGetPolicyDetails(action) {
  const { policyNo, policyEffectiveDate, policyExpirationDate } = action.payload
  const user = yield select(getIdentityUser)
  try {
    yield put(setCloneAppSearchStatus('searching'))
    const response = yield call(retrievePolicyDetails, { policyNo, policyEffectiveDate, policyExpirationDate }, user)
    const policyDetails = _.get(response, 'RetrievePolicyDetailsResponse.RLSPolicy', null)

    yield put(setClonedPolicy(policyDetails))
    yield put(setCloneAppSearchStatus('success'))
  } catch (err) {
    const message = err.message
    const notificationMessage = getToggles().ENABLE_RED_TRUCK_ERROR_MSG
      ? { type: 'GenericError', message: message }
      : { type: 'GenericError' }
    yield put(setClonedPolicy(null))
    yield put(setProcessingSuccess())
    yield put(setNotificationMessage(notificationMessage))
    yield put(setCloneAppSearchStatus('error'))
  }
}

// remote selling

export function* redirectMaturityValidate(history) {
  const maturityValidate = yield select(getMaturityValidate)
  if (!maturityValidate.maturityStatus) {
    yield put(selectPolicyOwner(''))
    return
  }
  yield put(resetQuoteFormAfterMaturityValidate())

  let redirectPath
  const productCode = yield select(getSelectedDisplayProductCode)
  if (
    Object.values(VALUE.LIFEREADY_SIO_CODE)
      .concat(Object.values(VALUE.ISHIELD_SIO_CODE))
      .concat(VALUE.CODE_12PL_SIO)
      .includes(productCode)
  ) {
    redirectPath = ROUTE_PATH.PRODUCT_SIO.INSURED_INFORMATION
  } else if (productCode === VALUE.CODE_12PL_GIO) {
    redirectPath = ROUTE_PATH.PRODUCT_GIO_PREUW.INSURED_INFORMATION
  } else {
    maturityValidate.isBuyForOwner
      ? yield put(selectPolicyOwner(DEFAULTS.SELF))
      : yield put(selectPolicyOwner(DEFAULTS.SPOUSE))
    redirectPath = ROUTE_PATH.S7CC.SELECT_POLICY_OWNER
  }

  const path = isFNAPath(history.location.pathname)
    ? fnaPath(redirectPath)
    : isQuickQuotePath(history.location.pathname)
    ? qqPath(redirectPath)
    : history.location.pathname

  history.push(path)
}

export function* checkToggleFromMiddleware(esubStore) {
  const toggleMW = yield middlewareSvc.getRequest('config', { ignoreAccessToken: true })
  const statusEnhancedAddress = _.get(toggleMW, 'data.enableEnhancedAddress', true)
  const { dispatch } = esubStore
  yield call(dispatch, updateEnhancedAddressStatus(statusEnhancedAddress))
}

export function* checkApplicationUpdateFromMiddleware(history) {}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 * @param {ReturnType<import('identity/userManager').default>} userManager
 * @param {ReturnType<import('e-submission/store').default>} esubStore
 * @param {ReturnType<import('history').createBrowserHistory>} history
 */
export function* rootSaga(serviceWrapper, userManager, esubStore, history) {
  const CORE_DATA_SUCCESS_ACTIONS = [
    GET_PRODUCTS_SUCCESS,
    GET_RIDERS_SUCCESS,
    GET_OCCUPATIONS_SUCCESS,
    GET_MODEL_FACTORS_SUCCESS,
    GET_DISTRIBUTOR_GROUPS_SUCCESS,
  ]

  const CORE_DATA_FAILURE_ACTIONS = [
    CONNECT_TO_DATABASE_FAILED,
    GET_PRODUCTS_FAILED,
    GET_RIDERS_FAILED,
    GET_OCCUPATIONS_FAILED,
    GET_MODEL_FACTORS_FAILED,
    GET_DISTRIBUTOR_GROUPS_FAILED,
  ]

  const CORE_USER_ACTIONS = [USER_EXPIRED, USER_FOUND, UPDATE_USER]

  yield all([
    takeEvery(LOGIN_REQUEST, onLogIn, userManager),
    takeEvery(LOGOUT_REQUEST, onLogOut, userManager, history),
    takeEvery(INITIALISE_APPLICATION_REQUEST, onInitialiseApplicationRequest),
    takeEvery(CONNECT_TO_DATABASE_REQUEST, onConnectToDatabaseRequest, serviceWrapper),
    takeEvery(CORE_DATA_SUCCESS_ACTIONS, checkIfApplicationDataIsReady, serviceWrapper),
    takeEvery(CORE_DATA_FAILURE_ACTIONS, onAnyRequiredDataRequestFailed),
    takeLatest(CORE_USER_ACTIONS, serviceWrapper.products.COMMON.updateProducts),
    takeEvery(RELOAD_APPLICATION_REQUEST, onReloadApplicationRequest, window.location),
    takeEvery(UPDATE_APPLICATION_REQUEST, onUpdateApplicationRequest, window.location),
    takeLatest(START_STANDARD_SELLING, startEsub, esubStore, history, serviceWrapper),
    takeLatest(START_REMOTE_SELLING, startEsub, esubStore, history, serviceWrapper),
    takeLatest(ENABLE_FNA_PHASE, startFnaPhase, history),
    takeLatest(SET_ACCEPT_ILP_SALE_SHEET, handleAcceptIlpSlaeSheet, history),
    takeLatest(BACK_TO_HOME, handleBackToHome, history),
    takeLatest(CLICK_HOME_BUTTON, onHomeButtonClick, history),
    takeLatest(UPDATE_MATURITY_VALIDATE, redirectMaturityValidate, history),
    takeLatest(CHECK_VERSION_APP, checkApplicationUpdateFromMiddleware, history),
    takeLatest(SEARCH_APP_LIST_BY_ID, handleSearchApplicationListById),
    takeLatest(GET_EIP_POLICY_DETAILS, onGetPolicyDetails),
    takeLatest(REDIRECT_MA_PAGE, goToMaPage, history),
    takeLatest(REDIRECT_OPTY_PAGE, goToOptyPage, history),
    takeLatest(RESET_QUOTE_FORM, checkToggleFromMiddleware, esubStore),
    spawn(uiSagas, serviceWrapper),
    spawn(fnaSagas),
    spawn(fnaNeedGapSagas),
    spawn(fnaMarketConductSagas),
    spawn(fnaSuitabilitySagas),
    spawn(InsuredInformationSagas, serviceWrapper),
    spawn(benefitIllustrationSelectionSagas, serviceWrapper),
    spawn(notificationSagas, serviceWrapper, history),
    spawn(productWholeLifeCoveragePlanSagas, serviceWrapper),
    spawn(productCommonBenefitIllustrationSagas, serviceWrapper),
    spawn(productLoanCoveragePlanSagas, serviceWrapper),
    spawn(productHealthCoveragePlanSagas, serviceWrapper),
    spawn(productFlexiHealthCoveragePlanSagas),
    spawn(productIHealthyUltraCoveragePlanSagas, serviceWrapper),
    spawn(productIHealthyUltraSagas, serviceWrapper),
    spawn(productLifeReadySagas, serviceWrapper),
    spawn(productLifeProtectPlusSagas, serviceWrapper),
    spawn(productBumnanReadySagas, serviceWrapper),
    spawn(productSIOSagas, serviceWrapper),
    spawn(productTermLifeSagas, serviceWrapper),
    spawn(productInvestmentBenefitTermLifeSagas, serviceWrapper),
    spawn(productMRTASagas, serviceWrapper),
    spawn(productPASagas, serviceWrapper),
    spawn(productLoanBenefitIllustrationSagas, serviceWrapper),
    spawn(productLifeSaveProSagas, serviceWrapper),
    spawn(productLifeSaveProBenefitSagas, serviceWrapper),
    spawn(productLifeProtect18Sagas, serviceWrapper),
    spawn(productPerfectLifeSagas, serviceWrapper),
    spawn(productHealthToppingSagas, serviceWrapper),
    spawn(productSukhapabDekDeeSagas, serviceWrapper),
    spawn(productInvestmentCoveragePlanSagas, serviceWrapper),
    spawn(analyticsSagas),
    spawn(fundAllocationSagas, serviceWrapper),
    spawn(productInvestmentBenefitIllustrationSagas, serviceWrapper),
    spawn(productSelectionSagas, history, serviceWrapper),
    spawn(productProtectionSagas),
    spawn(productSavingSagas, serviceWrapper),
    spawn(optySagas, services),
    spawn(mwPlusSagas, history),
    spawn(watchAPIError),
    spawn(core),
  ])
}
